'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import {
  createGalleryEvent,
  deleteGalleryEvent,
  getGalleryEvents,
  updateGalleryEvent,
  uploadGalleryImage,
} from '@/lib/actions';
import type { GalleryEventDoc, GalleryEventImage } from '@/lib/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Images,
  Loader2,
  Plus,
  Trash2,
  Pencil,
  Clipboard,
  Upload,
  ArrowUp,
  ArrowDown,
  Calendar,
  GitCommit,
  X,
  Star,
} from 'lucide-react';
import { cn } from '@/lib/utils';

const uid = () => `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;

// ─── Editor de un evento ─────────────────────────────────────────────────────

function EventEditor({
  event,
  onClose,
  onSaved,
}: {
  event: GalleryEventDoc;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { toast } = useToast();
  const [title, setTitle] = useState(event.title);
  const [date, setDate] = useState(event.date);
  const [modVersion, setModVersion] = useState(event.modVersion || '');
  const [images, setImages] = useState<GalleryEventImage[]>(event.images || []);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const dropRef = useRef<HTMLDivElement>(null);

  const subir = useCallback(
    async (files: (File | Blob)[]) => {
      const validos = files.filter((f) => f.type?.startsWith('image/'));
      if (!validos.length) return;
      setUploading((n) => n + validos.length);
      const nuevas: GalleryEventImage[] = [];
      for (const f of validos) {
        const res = await uploadGalleryImage(f, (f as File).name || `pegada_${uid()}.png`);
        if (res.success && res.url) nuevas.push({ id: uid(), src: res.url, alt: '' });
        else toast({ title: 'Error', description: res.message, variant: 'destructive' });
        setUploading((n) => n - 1);
      }
      if (nuevas.length) {
        setImages((prev) => [...prev, ...nuevas]);
        toast({ title: 'Listo', description: `${nuevas.length} foto(s) añadida(s).` });
      }
    },
    [toast],
  );

  // Pegar desde el portapapeles: cualquier Ctrl+V mientras el editor está
  // abierto (aunque no haya foco en el recuadro) mete las fotos del portapapeles.
  useEffect(() => {
    const onPaste = (e: ClipboardEvent) => {
      const target = e.target as HTMLElement | null;
      // Si se está pegando texto dentro de un campo, no lo interceptamos.
      const enCampo = target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA');
      const items = Array.from(e.clipboardData?.items || []);
      const ficheros = items
        .filter((i) => i.kind === 'file' && i.type.startsWith('image/'))
        .map((i) => i.getAsFile())
        .filter(Boolean) as File[];
      if (!ficheros.length) return;
      if (enCampo) return;
      e.preventDefault();
      subir(ficheros);
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [subir]);

  const mover = (i: number, dir: -1 | 1) => {
    setImages((prev) => {
      const j = i + dir;
      if (j < 0 || j >= prev.length) return prev;
      const next = [...prev];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  };

  const guardar = async () => {
    if (!title.trim() || !date.trim()) {
      toast({ title: 'Faltan datos', description: 'El título y la fecha son obligatorios.', variant: 'destructive' });
      return;
    }
    setSaving(true);
    const res = await updateGalleryEvent(event.id, {
      title: title.trim(),
      date: date.trim(),
      modVersion: modVersion.trim(),
      images,
    });
    setSaving(false);
    if (res.success) {
      toast({ title: 'Guardado', description: res.message });
      onSaved();
      onClose();
    } else {
      toast({ title: 'Error', description: res.message, variant: 'destructive' });
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="flex max-h-[90dvh] max-w-4xl flex-col p-0">
        <DialogHeader className="px-6 pt-6">
          <DialogTitle className="flex items-center gap-2">
            <Images className="h-5 w-5 text-primary" />
            Editar evento de la galería
          </DialogTitle>
          <DialogDescription>
            Pega fotos con <kbd className="rounded border border-white/20 bg-white/10 px-1">Ctrl</kbd>+
            <kbd className="rounded border border-white/20 bg-white/10 px-1">V</kbd>, arrástralas al recuadro o súbelas
            desde archivo.
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-6">
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-2 sm:col-span-2">
              <Label>Título</Label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Magna 'Testigos de la Esperanza'" />
            </div>
            <div className="space-y-2">
              <Label>Fecha</Label>
              <Input value={date} onChange={(e) => setDate(e.target.value)} placeholder="Septiembre 2025" />
            </div>
            <div className="space-y-2">
              <Label>Versión del mod</Label>
              <Input value={modVersion} onChange={(e) => setModVersion(e.target.value)} placeholder="3.0" />
            </div>
          </div>

          {/* Zona de pegado / arrastre */}
          <div
            ref={dropRef}
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              subir(Array.from(e.dataTransfer.files));
            }}
            onClick={() => fileRef.current?.click()}
            className={cn(
              'mt-5 flex cursor-pointer flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed p-8 text-center transition-colors',
              dragOver ? 'border-primary bg-primary/10' : 'border-white/15 bg-white/[0.02] hover:border-primary/40',
            )}
          >
            <div className="flex items-center gap-3 text-primary">
              <Clipboard className="h-6 w-6" />
              <Upload className="h-6 w-6" />
            </div>
            <p className="text-sm font-semibold">Pega, arrastra o haz clic para añadir fotos</p>
            <p className="text-xs text-muted-foreground">
              Copia una captura del juego y pulsa Ctrl+V aquí mismo. Se pueden añadir varias a la vez.
            </p>
            {uploading > 0 && (
              <p className="flex items-center gap-2 text-xs font-semibold text-primary">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Subiendo {uploading} foto(s)…
              </p>
            )}
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => {
                subir(Array.from(e.target.files || []));
                e.target.value = '';
              }}
            />
          </div>

          {/* Rejilla de fotos */}
          <div className="mt-5 flex items-center justify-between">
            <p className="text-sm font-semibold">
              Fotos <span className="text-muted-foreground">({images.length})</span>
            </p>
            {images.length > 0 && (
              <Button variant="ghost" size="sm" className="text-destructive" onClick={() => setImages([])}>
                <Trash2 className="h-3.5 w-3.5" />
                Quitar todas
              </Button>
            )}
          </div>

          <div className="mt-2 grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-5">
            <AnimatePresence>
              {images.map((img, i) => (
                <motion.div
                  key={img.id}
                  layout
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  className="group relative aspect-square overflow-hidden rounded-xl border border-white/10"
                >
                  <img src={img.src} alt="" className="h-full w-full object-cover" />
                  {i === 0 && (
                    <span className="absolute left-1 top-1 flex items-center gap-1 rounded-full bg-primary/90 px-1.5 py-px text-[0.55rem] font-black uppercase text-primary-foreground">
                      <Star className="h-2.5 w-2.5" />
                      Portada
                    </span>
                  )}
                  <div className="absolute inset-0 flex items-end justify-center gap-1 bg-gradient-to-t from-black/80 to-transparent p-1 opacity-0 transition-opacity group-hover:opacity-100">
                    <button
                      type="button"
                      onClick={() => mover(i, -1)}
                      disabled={i === 0}
                      className="grid h-6 w-6 place-items-center rounded-md bg-white/15 text-white hover:bg-white/30 disabled:opacity-30"
                    >
                      <ArrowUp className="h-3 w-3" />
                    </button>
                    <button
                      type="button"
                      onClick={() => mover(i, 1)}
                      disabled={i === images.length - 1}
                      className="grid h-6 w-6 place-items-center rounded-md bg-white/15 text-white hover:bg-white/30 disabled:opacity-30"
                    >
                      <ArrowDown className="h-3 w-3" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setImages((prev) => prev.filter((p) => p.id !== img.id))}
                      className="grid h-6 w-6 place-items-center rounded-md bg-rose-600/80 text-white hover:bg-rose-600"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>

          {images.length === 0 && (
            <p className="py-6 text-center text-sm text-muted-foreground">Todavía no hay fotos en este evento.</p>
          )}
        </div>

        <DialogFooter className="border-t border-white/10 px-6 py-4">
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={guardar} disabled={saving || uploading > 0}>
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            Guardar cambios
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Página ──────────────────────────────────────────────────────────────────

export default function AdminGaleriaPage() {
  const { userData, loading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [events, setEvents] = useState<GalleryEventDoc[]>([]);
  const [cargando, setCargando] = useState(true);
  const [editing, setEditing] = useState<GalleryEventDoc | null>(null);
  const [borrando, setBorrando] = useState<GalleryEventDoc | null>(null);
  const [creando, setCreando] = useState(false);
  const [nuevo, setNuevo] = useState({ title: '', date: '', modVersion: '' });
  const [creandoLoading, setCreandoLoading] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    setEvents(await getGalleryEvents());
    setCargando(false);
  }, []);

  useEffect(() => {
    if (loading) return;
    if (userData?.role !== 'admin') {
      navigate('/jartinet/home');
      return;
    }
    cargar();
  }, [loading, userData, navigate, cargar]);

  const totalFotos = useMemo(() => events.reduce((n, e) => n + (e.images?.length || 0), 0), [events]);

  const crear = async () => {
    if (!nuevo.title.trim() || !nuevo.date.trim()) {
      toast({ title: 'Faltan datos', description: 'Pon al menos título y fecha.', variant: 'destructive' });
      return;
    }
    setCreandoLoading(true);
    const res = await createGalleryEvent(nuevo);
    setCreandoLoading(false);
    if (res.success) {
      toast({ title: 'Creado', description: res.message });
      setCreando(false);
      setNuevo({ title: '', date: '', modVersion: '' });
      const lista = await getGalleryEvents();
      setEvents(lista);
      const creado = lista.find((e) => e.id === res.id);
      if (creado) setEditing(creado);
    } else {
      toast({ title: 'Error', description: res.message, variant: 'destructive' });
    }
  };

  const eliminar = async () => {
    if (!borrando) return;
    const res = await deleteGalleryEvent(borrando.id);
    toast({
      title: res.success ? 'Eliminado' : 'Error',
      description: res.message,
      variant: res.success ? undefined : 'destructive',
    });
    setBorrando(null);
    cargar();
  };

  if (loading || cargando) {
    return (
      <div className="flex h-[60vh] items-center justify-center">
        <Loader2 className="h-10 w-10 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-8 pb-10">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-primary/15 via-card/60 to-transparent p-7">
        <div className="pointer-events-none absolute -right-20 -top-20 h-64 w-64 rounded-full bg-primary/20 blur-3xl" />
        <div className="relative flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <div className="grid h-12 w-12 place-items-center rounded-2xl border border-primary/30 bg-primary/10">
                <Images className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h1 className="font-headline text-3xl font-bold md:text-4xl">Galería</h1>
                <p className="text-sm text-muted-foreground">Eventos y fotos de la galería pública de Jartiland.</p>
              </div>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs font-semibold">
                {events.length} evento{events.length === 1 ? '' : 's'}
              </span>
              <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs font-semibold">
                {totalFotos} foto{totalFotos === 1 ? '' : 's'}
              </span>
            </div>
          </div>
          <Button size="lg" onClick={() => setCreando(true)}>
            <Plus className="h-5 w-5" />
            Nuevo evento
          </Button>
        </div>
      </div>

      {events.length === 0 && (
        <div className="rounded-2xl border border-dashed border-white/15 py-16 text-center">
          <Images className="mx-auto h-10 w-10 text-muted-foreground/50" />
          <p className="mt-3 font-semibold">Todavía no hay eventos creados aquí</p>
          <p className="mt-1 text-sm text-muted-foreground">
            Los eventos históricos (Magna, Rocío…) siguen publicados desde el código; los que crees aquí saldrán primero.
          </p>
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {events.map((ev) => (
          <motion.div
            key={ev.id}
            layout
            whileHover={{ y: -4 }}
            className="group relative overflow-hidden rounded-2xl border border-white/10 bg-card/40"
          >
            <div className="relative aspect-video overflow-hidden bg-black/40">
              {ev.images?.[0] ? (
                <img
                  src={ev.images[0].src}
                  alt=""
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              ) : (
                <div className="grid h-full place-items-center text-muted-foreground/50">
                  <Images className="h-8 w-8" />
                </div>
              )}
              <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-card to-transparent" />
              <span className="absolute right-2 top-2 rounded-full bg-black/70 px-2 py-0.5 text-[0.65rem] font-bold text-white backdrop-blur">
                {ev.images?.length || 0} fotos
              </span>
            </div>

            <div className="p-4">
              <h3 className="truncate font-bold">{ev.title}</h3>
              <div className="mt-1.5 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Calendar className="h-3 w-3 text-primary" />
                  {ev.date}
                </span>
                {ev.modVersion && (
                  <span className="flex items-center gap-1">
                    <GitCommit className="h-3 w-3 text-primary" />v{ev.modVersion}
                  </span>
                )}
              </div>

              <div className="mt-4 flex gap-2">
                <Button size="sm" className="flex-1" onClick={() => setEditing(ev)}>
                  <Pencil className="h-3.5 w-3.5" />
                  Editar y añadir fotos
                </Button>
                <Button size="icon" variant="outline" className="text-destructive" onClick={() => setBorrando(ev)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Crear */}
      <Dialog open={creando} onOpenChange={setCreando}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nuevo evento de galería</DialogTitle>
            <DialogDescription>Después podrás pegar las fotos directamente desde el portapapeles.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Título</Label>
              <Input
                value={nuevo.title}
                onChange={(e) => setNuevo({ ...nuevo, title: e.target.value })}
                placeholder="Semana Santa 2026"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Fecha</Label>
                <Input
                  value={nuevo.date}
                  onChange={(e) => setNuevo({ ...nuevo, date: e.target.value })}
                  placeholder="Abril 2026"
                />
              </div>
              <div className="space-y-2">
                <Label>Versión del mod</Label>
                <Input
                  value={nuevo.modVersion}
                  onChange={(e) => setNuevo({ ...nuevo, modVersion: e.target.value })}
                  placeholder="3.0"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCreando(false)}>
              Cancelar
            </Button>
            <Button onClick={crear} disabled={creandoLoading}>
              {creandoLoading && <Loader2 className="h-4 w-4 animate-spin" />}
              Crear y añadir fotos
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {editing && <EventEditor event={editing} onClose={() => setEditing(null)} onSaved={cargar} />}

      <AlertDialog open={!!borrando} onOpenChange={(o) => !o && setBorrando(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar «{borrando?.title}»?</AlertDialogTitle>
            <AlertDialogDescription>
              El evento desaparecerá de la galería pública. Las fotos seguirán en Storage.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={eliminar}>Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
